import { c as _c } from "react/compiler-runtime";
import { LayoutConfigProvider } from '@newfront-insurance/admin-ui/layout';
import { colors } from '@newfront-insurance/core-ui';
import { cn } from '@newfront-insurance/core-ui/v2';
import { useNullableProvider } from '@newfront-insurance/react-provision';
import { useEffect, useState, useRef } from 'react';
import { NovaIcon } from '../../icons';
interface FloatingButtonProps {
  onClick: () => void;
  hasNotifications?: boolean;
  isTransparent?: boolean;
  label?: string;
  secondaryLabel?: string;
  className?: string;
  icon?: React.ReactNode;
}
function DragHandle(t0) {
  const $ = _c(2);
  const {
    color: t1
  } = t0;
  const color = t1 === undefined ? colors.brand[500] : t1;
  let t2;
  if ($[0] !== color) {
    t2 = <svg width="9" height="16" viewBox="0 0 9 16" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="3" height="3" transform="translate(0 0.5)" fill={color} /><rect width="3" height="3" transform="translate(6 0.5)" fill={color} /><rect width="3" height="3" transform="translate(0 6.5)" fill={color} /><rect width="3" height="3" transform="translate(6 6.5)" fill={color} /><rect width="3" height="3" transform="translate(0 12.5)" fill={color} /><rect width="3" height="3" transform="translate(6 12.5)" fill={color} /></svg>;
    $[0] = color;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  return t2;
}
export function FloatingButton({
  onClick,
  hasNotifications,
  isTransparent,
  label,
  secondaryLabel,
  className,
  icon
}: FloatingButtonProps) {
  // Get the current app from the layout context (optional - may not be available in all apps)
  const layoutConfig = useNullableProvider(LayoutConfigProvider);
  const currentApp = layoutConfig?.currentApp;
  const storageKey = currentApp ? `nova-button-position-${currentApp}` : 'nova-button-position';
  const hasTextAnimation = Boolean(secondaryLabel);
  const [position, setPosition] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(storageKey);
      return saved ? parseInt(saved, 10) : 36; // Default 36px from right
    }
    return 36;
  });
  const [isDragging, setIsDragging] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dragStartX = useRef(0);
  const startRight = useRef(0);
  const hasDragged = useRef(false);

  /** Adds keyboard shortcuts to the floating button to fire the onClick handler when Command/Ctrl + K is pressed */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === 'k') {
        // If Command is already pressed when K is pressed, trigger the click
        if (e.metaKey || e.ctrlKey) {
          e.preventDefault();
          onClick();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClick]);

  // Handle dragging
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      const deltaX = dragStartX.current - e.clientX;
      const newRight = Math.max(0, Math.min(window.innerWidth - (buttonRef.current?.offsetWidth || 0), startRight.current + deltaX));
      setPosition(newRight);

      // Mark that we've actually dragged (not just clicked)
      if (Math.abs(deltaX) > 5) {
        hasDragged.current = true;
      }
    };
    const handleMouseUp = () => {
      if (isDragging) {
        setIsDragging(false);
        // Save position to localStorage
        localStorage.setItem(storageKey, position.toString());

        // Prevent the click event if we actually dragged
        if (hasDragged.current) {
          setTimeout(() => {
            hasDragged.current = false;
          }, 100);
        }
      }
    };
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, position]);

  // Ensure the button remains visible within the viewport when zoom/resize changes
  useEffect(() => {
    const ensureVisible = () => {
      const buttonWidth = buttonRef.current?.offsetWidth || 0;
      const maxRight = Math.max(0, window.innerWidth - buttonWidth);
      setPosition(prev => {
        const clamped = Math.max(0, Math.min(prev, maxRight));
        if (clamped !== prev) {
          try {
            localStorage.setItem(storageKey, clamped.toString());
          } catch {
            // noop
          }
        }
        return clamped;
      });
    };

    // Run after initial render to ensure we can measure the element
    const raf = requestAnimationFrame(ensureVisible);
    window.addEventListener('resize', ensureVisible);
    window.addEventListener('orientationchange', ensureVisible);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', ensureVisible);
      window.removeEventListener('orientationchange', ensureVisible);
    };
  }, [storageKey]);
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    dragStartX.current = e.clientX;
    startRight.current = position;
  };
  return <button ref={buttonRef} type="button" aria-label="Nova Assist" className={cn('fixed bottom-0 z-[1000]', 'h-10 w-fit', 'rounded-t-sm shadow-lg', 'text-steel-100', 'shadow-floating-bar', 'flex items-center justify-between', 'text-sm font-semibold', 'select-none', 'p-0', 'hover:shadow-rebrand-400/20 focus:outline-hidden', 'bg-lime-blue-gradient', className, {
    'cursor-move': isDragging,
    // Make the floating button transparent/click through. Chat can still be opened by pressing the command+k shortcut.
    'pointer-events-none opacity-0': isTransparent
  })} style={{
    right: `${position}px`,
    boxShadow: '3.013px 3.013px 24.1px 0 rgba(38, 45, 70, 0.15)'
  }} onClick={e => {
    // Prevent click if we just finished dragging
    if (hasDragged.current) {
      e.preventDefault();
      return;
    }
    onClick();
  }}>
      {/* Shimmer effect overlay - plays once on mount only when secondaryLabel is present */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-t-sm" aria-hidden="true">
        <div className={cn('absolute inset-0', secondaryLabel && 'animate-[shimmer_2s_2_forwards]')} style={{
        background: 'linear-gradient(90deg, transparent 0%, transparent 30%, rgba(255,255,255,0.15) 50%, transparent 70%, transparent 100%)'
      }} />
      </div>

      <div className="flex h-full w-full items-center justify-between gap-2 px-4">
        <div className="group flex h-full items-center transition-colors" role="button" tabIndex={0} aria-label="Drag to move" onMouseDown={handleMouseDown} onKeyDown={e => {
        // Optionally, allow keyboard drag with space/enter
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          setIsDragging(true);
          dragStartX.current = 0;
          startRight.current = position;
        }
      }}>
          <div className={cn('items opacity-100 group-hover:opacity-0', {
          'opacity-0': isDragging // Ensures the icon doesn't show up while dragging and the cursor leaves the button
        })}>
            {icon || <NovaIcon />}
          </div>
          <div className={cn('absolute left-4.5 opacity-0 group-hover:opacity-100', {
          'opacity-100': isDragging
        })}>
            <DragHandle />
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasTextAnimation ? <div className="relative h-5 overflow-hidden">
              <div className="animate-text-cycle-up flex flex-col items-center">
                <span className="text-brand-500 flex h-5 items-center font-semibold whitespace-nowrap">
                  {secondaryLabel}
                </span>
                <span className="text-brand-500 flex h-5 items-center font-semibold whitespace-nowrap">
                  {label || 'Ask Me'}
                </span>
              </div>
            </div> : <span className="text-brand-500 font-semibold tracking-widest">{label || 'Nova Assist'}</span>}
        </div>
        <span className={cn(['bg-rebrand-400 absolute top-[-8px] right-[-4px] h-[16px] w-[16px] rounded-full border-2 border-white', {
        block: hasNotifications,
        hidden: !hasNotifications
      }])} />
      </div>
    </button>;
}